import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { VariantProps, cva } from "class-variance-authority"
import { PanelLeftIcon } from "lucide-react"

import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

const SIDEBAR_COOKIE_NAME = "sidebar_state"
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_WIDTH = "16rem"
const SIDEBAR_WIDTH_MOBILE = "18rem"
const SIDEBAR_WIDTH_ICON = "3rem"
const SIDEBAR_KEYBOARD_SHORTCUT = "b"

type SidebarContextProps = {
  state: "expanded" | "collapsed"
  open: boolean
  setOpen: (open: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
  isDraggingRail: boolean
  setIsDraggingRail: (dragging: boolean) => void
}

const SidebarContext = React.createContext<SidebarContextProps | null>(null)

function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }

  return context
}

interface SidebarPersistenceContext {
  persistSidebarWidth?: (width: number) => void;
  persistSidebarCollapsed?: (collapsed: boolean) => void;
}
const SidebarPersistenceContext = React.createContext<SidebarPersistenceContext>({});

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  persistSidebarWidth,
  persistSidebarCollapsed,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  persistSidebarWidth?: (width: number) => void
  persistSidebarCollapsed?: (collapsed: boolean) => void
}) {
  const isMobile = useIsMobile()
  
  // MOBILE SIDEBAR STATE HANDLING - completely rewritten for reliability
  // Force fetch from localStorage every render to ensure latest value
  const getMobileStateFromStorage = React.useCallback(() => {
    try {
      return localStorage.getItem('sidebarMobileOpen') === 'true';
    } catch (e) {
      return false;
    }
  }, []);
  
  // Initialize mobile state from localStorage
  const [openMobile, _setOpenMobile] = React.useState(getMobileStateFromStorage);
  
  // Listen for localStorage changes from other components/tabs
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'sidebarMobileOpen') {
        const newValue = e.newValue === 'true';
        _setOpenMobile(newValue);
      }
    };
    
    // Listen for custom toggle events
    const handleToggleEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      const newValue = customEvent.detail?.open;
      if (typeof newValue === 'boolean') {
        _setOpenMobile(newValue);
        localStorage.setItem('sidebarMobileOpen', String(newValue));
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sidebar-toggle', handleToggleEvent);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sidebar-toggle', handleToggleEvent);
    };
  }, [openMobile]);
  
  // Enhanced setOpenMobile that guarantees localStorage sync
  const setOpenMobile = React.useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(openMobile) : value;
    
    // Update React state first
    _setOpenMobile(newValue);
    
    // Then sync to localStorage
    try {
      localStorage.setItem('sidebarMobileOpen', String(newValue));
    } catch (e) {
      console.error('Failed to persist mobile sidebar state:', e);
    }
    
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('sidebar-toggle', {
      detail: { open: newValue }
    }));
  }, [openMobile])

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen)
  const open = openProp ?? _open
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value
      if (setOpenProp) {
        setOpenProp(openState)
      } else {
        _setOpen(openState)
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
    },
    [setOpenProp, open]
  )

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      // Force immediate state update and ensure propagation
      const newState = !openMobile;
      setOpenMobile(newState);
      
      // Also update the Layout component state through setOpenProp
      if (setOpenProp) {
        setOpenProp(newState);
      }
    } else {
      const newState = !open;
      setOpen(newState);
      // Also update parent state
      if (setOpenProp) {
        setOpenProp(newState);
      }
    }
  }, [isMobile, setOpen, setOpenMobile, openMobile, open, setOpenProp])

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault()
        toggleSidebar();
        (window as any).toggleSidebar = toggleSidebar;
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [toggleSidebar])

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? "expanded" : "collapsed"

  const [isDraggingRail, setIsDraggingRail] = React.useState(false);

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      isDraggingRail,
      setIsDraggingRail,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar, isDraggingRail]
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      <SidebarPersistenceContext.Provider value={{ persistSidebarWidth, persistSidebarCollapsed }}>
        <TooltipProvider delayDuration={0}>
          <div
            data-slot="sidebar-wrapper"
            style={
              {
                // '--sidebar-width': SIDEBAR_WIDTH,
                '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
                ...style
              } as React.CSSProperties
            }
            className={cn('group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh', className)}
            {...props}>
            {children}
          </div>
        </TooltipProvider>
      </SidebarPersistenceContext.Provider>
    </SidebarContext.Provider>
  );
}

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right"
  variant?: "sidebar" | "floating" | "inset"
  collapsible?: "offcanvas" | "icon" | "none"
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar()

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "flex h-full flex-col bg-sidebar text-sidebar-foreground",
          className,
        )}
        style={{ width: "var(--sidebar-width)" }}
        {...props}
      >
        {children}
      </div>
    )
  }

  if (isMobile) {
    // Mobile sidebar rendering logic
    const handleCloseSidebar = React.useCallback(() => {
      setOpenMobile(false);
      localStorage.setItem('sidebarMobileOpen', 'false');
    }, [setOpenMobile]);
    
    // Listen for the custom sidebar-toggle event
    React.useEffect(() => {
      const handleSidebarToggle = () => {
        // Sidebar toggle event detected
      };
      
      window.addEventListener('sidebar-toggle', handleSidebarToggle);
      return () => {
        window.removeEventListener('sidebar-toggle', handleSidebarToggle);
      };
    }, []);
    
    // Return nothing when sidebar should be closed
    if (!openMobile) {
      return null;
    }
    
    return (
      <div className="fixed inset-0 z-50 flex">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/50"
          onClick={handleCloseSidebar}
        />
        
        {/* Sidebar content */}
        <div 
          className={`bg-sidebar text-sidebar-foreground fixed inset-y-0 ${side === 'left' ? 'left-0' : 'right-0'} h-full w-3/4 sm:max-w-sm p-0 border-r flex flex-col animate-in slide-in-from-${side === 'left' ? 'left' : 'right'}`}
          style={{ width: SIDEBAR_WIDTH_MOBILE }}
        >
          <div className="sr-only">
            <h2>Sidebar</h2>
            <p>Displays the mobile sidebar.</p>
          </div>
          
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 rounded-full h-8 w-8 flex items-center justify-center hover:bg-gray-700/10"
            onClick={() => {
              setOpenMobile(false);
              localStorage.setItem('sidebarMobileOpen', 'false');
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4">
              <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
            </svg>
            <span className="sr-only">Close</span>
          </button>
          
          <div className="flex h-full w-full flex-col overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="group peer text-sidebar-foreground hidden md:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          "relative bg-transparent transition-[width] duration-200 ease-linear",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)"
        )}
        style={{ width: 'var(--sidebar-width)' }}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          "fixed inset-y-0 z-10 hidden h-svh transition-[left,right,width] duration-200 ease-linear md:flex",
          side === "left"
            ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
            : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
          // Adjust the padding for floating and inset variants.
          variant === "floating" || variant === "inset"
            ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l",
          className
        )}
        style={{ width: 'var(--sidebar-width)' }}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="bg-sidebar group-data-[variant=floating]:border-sidebar-border flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm"
        >
          {children}
        </div>
      </div>
    </div>
  )
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar, isMobile, openMobile, setOpenMobile } = useSidebar()

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon"
      className={cn("size-7", className)}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        
        // Execute user's onClick if provided
        onClick?.(event);
        
        // Direct approach for mobile - bypass toggleSidebar
        if (isMobile) {
          // Directly toggle mobile state with force refresh
          const newState = !openMobile;
          
          // First update context state
          setOpenMobile(newState);
          
          // Then localStorage (for persistence)
          localStorage.setItem('sidebarMobileOpen', String(newState));
          
          // Force re-render by dispatching a custom event
          window.dispatchEvent(new CustomEvent('sidebar-toggle', { detail: { open: newState }}));
        } else {
          // Use normal toggle for desktop
          toggleSidebar();
        }
      }}
      type="button"
      {...props}
    >
      <PanelLeftIcon />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
}

function SidebarRail({ className, ...props }: React.ComponentProps<"button">) {
  const { toggleSidebar, isDraggingRail, setIsDraggingRail } = useSidebar()
  const { persistSidebarWidth, persistSidebarCollapsed } = React.useContext(SidebarPersistenceContext);

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsDraggingRail(true)

    // Disable transitions on sidebar container and gap
    const sidebarContainer = document.querySelector('[data-slot="sidebar-container"]') as HTMLElement | null
    const sidebarGap = document.querySelector('[data-slot="sidebar-gap"]') as HTMLElement | null
    sidebarContainer?.classList.add('no-transition')
    sidebarGap?.classList.add('no-transition')

    const rect = event.currentTarget.getBoundingClientRect()
    const initialX = event.clientX
    const initialWidth = rect.width

    const handleMouseMove = (event: MouseEvent) => {
      const sidebarContainer = document.querySelector('[data-slot="sidebar-container"]') as HTMLElement | null
      const sidebarLeft = sidebarContainer?.getBoundingClientRect().left ?? 0
      const minWidth = 100
      const maxWidth = 500
      const newWidth = event.clientX - sidebarLeft
      let dontRun = false;
      let clampedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth)
      const sidebarEl = document.querySelector('[data-slot="sidebar"]') as HTMLElement | null;
      let isCollapsed = sidebarEl?.getAttribute('data-state') === 'collapsed';

      // Collapse if dragged under minWidth
      if (!(sidebarEl?.getAttribute('data-state') === 'collapsed') && newWidth < minWidth) {
        toggleSidebar();
        setIsDraggingRail(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.documentElement.style.setProperty('--sidebar-width', `50px`);
        // Also update :root for global propagation
        const root = document.querySelector(':root') as HTMLElement | null;
        if (root) {
          root.style.setProperty('--sidebar-width', `50px`);
        }
        const sidebarWrapper = document.querySelector('[data-slot="sidebar-wrapper"]') as HTMLElement | null;
        if (sidebarWrapper) {
          sidebarWrapper.style.setProperty('--sidebar-width', `50px`);
        }
        return;
      }

      // If sidebar is collapsed and user drags right past minWidth, expand and continue drag
      isCollapsed = sidebarEl?.getAttribute('data-state') === 'collapsed';
      if (isCollapsed && newWidth >= minWidth) {
        toggleSidebar();
        setTimeout(() => {
          // Overwrite everywhere
          document.documentElement.style.setProperty('--sidebar-width', `${clampedWidth}px`);
          const root = document.querySelector(':root') as HTMLElement | null;
          if (root) root.style.setProperty('--sidebar-width', `${clampedWidth}px`);
          const sidebarWrapper = document.querySelector('[data-slot="sidebar-wrapper"]') as HTMLElement | null;
          if (sidebarWrapper) sidebarWrapper.style.setProperty('--sidebar-width', `${clampedWidth}px`);
          // Also remove any inline style that hard-codes 50px if present
          if (sidebarWrapper?.style.getPropertyValue('--sidebar-width') === '50px') {
            sidebarWrapper.style.removeProperty('--sidebar-width');
            sidebarWrapper.style.setProperty('--sidebar-width', `${clampedWidth}px`);
          }
        }, 0);
      }

      document.documentElement.style.setProperty('--sidebar-width', `${clampedWidth}px`);
      const root = document.querySelector(':root') as HTMLElement | null;
      if (root) root.style.setProperty('--sidebar-width', `${clampedWidth}px`);
      const sidebarWrapper = document.querySelector('[data-slot="sidebar-wrapper"]') as HTMLElement | null;
      if (sidebarWrapper) sidebarWrapper.style.setProperty('--sidebar-width', `${clampedWidth}px`);

      // Debug current CSS variable value
      // const docValue = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-width')
      // const rootValue = root ? getComputedStyle(root).getPropertyValue('--sidebar-width') : undefined
      // console.debug('[SidebarRail] CSS Var set', { docValue, rootValue })
    }

    const handleMouseUp = () => {
      setIsDraggingRail(false)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      // Restore transitions on sidebar container and gap
      const sidebarContainer = document.querySelector('[data-slot="sidebar-container"]') as HTMLElement | null
      const sidebarGap = document.querySelector('[data-slot="sidebar-gap"]') as HTMLElement | null
      sidebarContainer?.classList.remove('no-transition')
      sidebarGap?.classList.remove('no-transition')
      // Debug final CSS variable value
      const docValue = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-width')
      const root = document.querySelector(':root') as HTMLElement | null
      const rootValue = root ? getComputedStyle(root).getPropertyValue('--sidebar-width') : undefined
      // Save width to user profile
      if (persistSidebarWidth && typeof persistSidebarWidth === 'function') {
        const width = parseInt(docValue, 10);
        if (!isNaN(width)) persistSidebarWidth(width);
      }
      // Save collapsed state
      const sidebarEl = document.querySelector('[data-slot="sidebar"]') as HTMLElement | null;
      const collapsed = sidebarEl?.getAttribute('data-state') === 'collapsed';
      if (persistSidebarCollapsed && typeof persistSidebarCollapsed === 'function') {
        persistSidebarCollapsed(collapsed);
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onMouseDown={handleMouseDown}
      // onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className
      )}
      {...props}
    />
  )
}

function SidebarInset({ className, ...props }: React.ComponentProps<"main">) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        "bg-background relative flex w-full flex-1 flex-col",
        "md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2",
        className
      )}
      {...props}
    />
  )
}

function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn("bg-background h-8 w-full shadow-none", className)}
      {...props}
    />
  )
}

function SidebarHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  )
}

function SidebarFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  )
}

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn("bg-sidebar-border mx-2 w-auto", className)}
      {...props}
    />
  )
}

function SidebarContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      {...props}
    />
  )
}

function SidebarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
      {...props}
    />
  )
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "div"

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        "text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className
      )}
      {...props}
    />
  )
}

function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 md:after:hidden",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  )
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn("w-full text-sm", className)}
      {...props}
    />
  )
}

function SidebarMenu({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn("flex w-full min-w-0 flex-col gap-1", className)}
      {...props}
    />
  )
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn("group/menu-item relative", className)}
      {...props}
    />
  )
}

const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline:
          "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean
  isActive?: boolean
  tooltip?: string | React.ComponentProps<typeof TooltipContent>
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  const Comp = asChild ? Slot : "button"
  const { isMobile, state } = useSidebar()

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  )

  if (!tooltip) {
    return button
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltip}
      />
    </Tooltip>
  )
}

function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean
  showOnHover?: boolean
}) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 md:after:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
        "peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0",
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        "text-sidebar-foreground pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums select-none",
        "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<"div"> & {
  showIcon?: boolean
}) {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`
  }, [])

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn("flex h-8 items-center gap-2 rounded-md px-2", className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  )
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        "border-sidebar-border mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l px-2.5 py-0.5",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  )
}

function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn("group/menu-sub-item relative", className)}
      {...props}
    />
  )
}

function SidebarMenuSubButton({
  asChild = false,
  size = "md",
  isActive = false,
  className,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean
  size?: "sm" | "md"
  isActive?: boolean
}) {
  const Comp = asChild ? Slot : "a"

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 outline-hidden focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
        "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  )
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
}
